import { createContext, type ReactNode, useContext } from 'react';

import type { PanelViewMode } from '../../stores/usePanelNavigation';

/**
 * ViewMode Context
 *
 * Phase 6-Next: 렌더링 컨텍스트(standalone/embedded)를 자식 컴포넌트에 전파합니다.
 *
 * @note 이 Context는 Selection/Navigation 상태를 소유하지 않는 렌더링 컨텍스트 전파용이므로,
 *       "Provider는 페이지/호스트에서만 선언" 원칙과 충돌하지 않습니다.
 *       ZonePanel 내부에서 사용해도 store 중첩/상태 분기 문제는 없습니다.
 *
 * @example
 * ```tsx
 * // Zone Detail 페이지 (standalone)
 * <ViewModeProvider viewMode="standalone">
 *   <ZoneDetailContent zoneId={zoneId} />
 * </ViewModeProvider>
 *
 * // Zone Panel 내부 (embedded)
 * <ViewModeProvider viewMode="embedded">
 *   <ZoneDetailContent zoneId={panelId} />
 * </ViewModeProvider>
 * ```
 */
const ViewModeContext = createContext<PanelViewMode>('standalone');

interface ViewModeProviderProps {
  children: ReactNode;
  viewMode: PanelViewMode;
}

/**
 * ViewMode Provider
 *
 * 렌더링 컨텍스트(standalone/embedded)를 자식 컴포넌트에 전파합니다.
 *
 * @param children - 자식 컴포넌트
 * @param viewMode - 현재 렌더링 모드 ('standalone' | 'embedded')
 */
export function ViewModeProvider({ children, viewMode }: ViewModeProviderProps) {
  return <ViewModeContext.Provider value={viewMode}>{children}</ViewModeContext.Provider>;
}

/**
 * 현재 렌더링 컨텍스트의 viewMode를 반환합니다.
 *
 * @returns 'standalone' (페이지) 또는 'embedded' (패널 내부)
 *
 * @example
 * ```tsx
 * const viewMode = useViewMode();
 * if (viewMode === 'embedded') {
 *   // 새 탭에서 열기 (패널 중첩 방지)
 *   window.open(url, '_blank');
 * } else {
 *   // 패널 열기
 *   publishSelection(scopeKey, { ids: [recordId], mode: 'single' });
 * }
 * ```
 */
export function useViewMode(): PanelViewMode {
  return useContext(ViewModeContext);
}
